import { writable } from 'svelte/store';
import { location } from 'svelte-spa-router';
import { getStartParam } from './telegram';
import { routeFromStartParam } from './bot-links';

/**
 * Whether the user has navigated *within* the SPA and therefore has a previous
 * screen to return to.
 *
 * A cold open starts with no in-app history. In particular a Telegram deep link
 * (`?startapp=material_<id>`) lands straight on /material/<id> via a single
 * `replace`, so window.history.back() has no in-app target — and inside the
 * Telegram WebView window.history.length is unreliable (it counts the WebView's
 * own entries), so the back button can't lean on it. We track navigation here
 * instead: TopBar uses this to decide between history.back() and an explicit
 * fallback href.
 */
export const hasInAppHistory = writable(false);

// The post-auth landing route. Its own location update (and only that one) must
// not count as in-app history — it's where the user *started*, not somewhere
// they can go back from.
let landingPath: string | null = null;

location.subscribe((loc) => {
  if (landingPath === null) return; // before the landing route is known
  if (loc === landingPath) return; // the landing's own location update
  hasInAppHistory.set(true);
});

/** Called by AuthorizationPage once it has routed to the landing screen. */
export function markLandingSettled(path: string): void {
  landingPath = path;
  hasInAppHistory.set(false);
}

// ---------------------------------------------------------------------------

let deepLinkConsumed = false;

/**
 * Resolve the deep-link target route (e.g. /material/<id>, /section/<id>)
 * exactly once per app load.
 *
 * Telegram keeps `WebApp.start_param` populated for the whole session, so
 * without this one-shot guard any later remount of AuthorizationPage (e.g. a
 * system back that pops to the initial entry) would re-read it and bounce the
 * user back into the deep-linked screen — trapping them. After the first read
 * we treat the deep link as spent.
 */
export function consumeDeepLinkRoute(): string | null {
  if (deepLinkConsumed) return null;
  deepLinkConsumed = true;
  return routeFromStartParam(getStartParam());
}
